// Tooltip helpers for the ACA module. Every explanation comes from the
// glossary so a term reads the same on every tab.
//
//   <Help term="ale" />                      info icon with the glossary text
//   <TermLabel term="line15">Line 15</TermLabel>   label + info icon
//   <Help text="custom explanation" />       ad-hoc text (use sparingly)
//   <CodeHelp code="2D" line={16}>2D</CodeHelp>    Line 14/16 code with its meaning
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ACA_GLOSSARY, LINE14_CODE_HELP, LINE16_CODE_HELP, type GlossaryKey } from "./acaGlossary";

interface HelpProps {
  term?: GlossaryKey;
  text?: string;
  /** Show the glossary label as a bold first line (default true when `term` is given). */
  title?: boolean;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
}

function HelpBody({ term, text, title = true }: Pick<HelpProps, "term" | "text" | "title">) {
  const entry = term ? ACA_GLOSSARY[term] : null;
  return (
    <div className="max-w-xs text-xs leading-relaxed space-y-1">
      {entry && title && <div className="font-semibold">{entry.label}</div>}
      <div>{text ?? entry?.text}</div>
    </div>
  );
}

/** Small info icon that explains a glossary term (or ad-hoc text) on hover/focus. */
export function Help({ term, text, title, className, side }: HelpProps) {
  if (!term && !text) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={term ? `What is ${ACA_GLOSSARY[term].label}?` : "More information"}
            className={`inline-flex items-center align-middle text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm ${className ?? ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side ?? "top"}><HelpBody term={term} text={text} title={title} /></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** A label followed by an info icon. */
export function TermLabel({ term, text, children, className }: HelpProps & { children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className ?? ""}`}>
      <span>{children}</span>
      <Help term={term} text={text} />
    </span>
  );
}

/** Wraps any element (badge, cell, chip) so hovering it shows the explanation. */
export function Explained({ term, text, title, children, side }: HelpProps & { children: ReactNode }) {
  if (!term && !text) return <>{children}</>;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild><span className="inline-flex cursor-help">{children}</span></TooltipTrigger>
        <TooltipContent side={side ?? "top"}><HelpBody term={term} text={text} title={title} /></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** A Line 14 or Line 16 code with its IRS meaning on hover. */
export function CodeHelp({ code, line, children, className }: { code: string | null | undefined; line: 14 | 16; children?: ReactNode; className?: string }) {
  const table = line === 14 ? LINE14_CODE_HELP : LINE16_CODE_HELP;
  const meaning = code ? table[code] : line === 16 ? ACA_GLOSSARY.line16Blank.text : undefined;
  const label = code ? `${code}` : "blank";
  if (!meaning) return <span className={className}>{children ?? label}</span>;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild><span className={`cursor-help ${className ?? ""}`}>{children ?? label}</span></TooltipTrigger>
        <TooltipContent side="top"><div className="max-w-xs text-xs leading-relaxed"><span className="font-semibold">Line {line} {label}:</span> {meaning}</div></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
